import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatBuyType, formatMapName } from '../../liveSession/lib/formatters';
import { formatClock } from '../../liveSession/lib/roundClock';
import { formatActionType, formatStrategyType, formatUpdated, roleBlurb, sideName } from '../lib/formatPlaybook';
import {
  ACTION_TYPES,
  BUY_TYPES,
  STRATOS_MAPS,
  STRATEGY_TYPES,
  UTILITY_TYPES,
  canonicalBuy,
  canonicalSide,
  parseClock,
  parseDuration,
  strategyWrite,
} from '../lib/authoring';
import { activationProblems, authoringWarnings } from '../lib/readiness';
import { mapKey } from '../lib/playbook';
import {
  createAction,
  createPlanStep,
  createRole,
  createUtilityUse,
  deleteAction,
  deletePlanStep,
  deleteRole,
  deleteStrategy,
  duplicateStrategy,
  getStrategy,
  listUtilityUses,
  updateAction,
  updatePlanStep,
  updateRole,
  updateStrategy,
} from '../strategyApi';
import type { StrategyActionDetail, StrategyDetail, StrategyRoleDetail, UtilityUseDetail, UtilityUseRecord } from '../types';
import AuthorGate from './AuthorGate';
import styles from './author.module.css';

export default function StrategyEditorPage() {
  const { strategyId = '' } = useParams();
  return (
    <AuthorGate crumb="Studio" wide>
      {(team) => <Editor teamId={team.id} strategyId={strategyId} />}
    </AuthorGate>
  );
}

function Editor({ teamId, strategyId }: { teamId: string; strategyId: string }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<StrategyDetail | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [lineups, setLineups] = useState<UtilityUseRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<'strategy' | 'role' | null>(null);
  const [newLineupFor, setNewLineupFor] = useState<string | null>(null);

  async function reload() {
    const [fresh, utilities] = await Promise.all([
      getStrategy(teamId, strategyId),
      listUtilityUses(teamId),
    ]);
    setDetail(fresh);
    setLineups(utilities);
    setRoleId((current) => current && fresh.roles.some((role) => role.id === current) ? current : fresh.roles[0]?.id ?? null);
    return fresh;
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the strategy');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // reload closes over teamId and strategyId only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, strategyId]);

  async function saveDetail(next: StrategyDetail, status = next.status) {
    setError(null);
    try {
      const saved = await updateStrategy(teamId, strategyId, strategyWrite(next, status));
      setDetail(saved);
      return saved;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      return null;
    }
  }

  function patch(partial: Partial<StrategyDetail>) {
    setDetail((current) => (current ? { ...current, ...partial } : current));
  }

  if (loading) return <p className={styles.status}>Loading strategy…</p>;
  if (!detail) return <p className={styles.error}>{error ?? 'Strategy not found.'}</p>;

  const roles = [...detail.roles].sort((a, b) => a.sortOrder - b.sortOrder);
  const role = roles.find((item) => item.id === roleId) ?? roles[0] ?? null;
  const steps = [...detail.planSteps].sort((a, b) => a.sortOrder - b.sortOrder);
  const problems = activationProblems(detail);
  const warnings = authoringWarnings(detail);
  const mapLineups = lineups.filter((lineup) => mapKey(lineup.map) === mapKey(detail.map));
  const actionCount = roles.reduce((sum, item) => sum + item.actions.length, 0);
  const callLineups = usedLineups(detail);
  const statusCopy = detail.status === 'active'
    ? 'Players can learn this, and it can be called for a live round.'
    : detail.status === 'inactive'
      ? 'Kept in the book. Hidden from learning and from new round calls.'
      : 'Only the strategist can see a draft. Players will not learn it yet.';
  const summary = problems.length > 0
    ? (detail.status === 'active'
      ? 'This is active, but it is missing something the team needs.'
      : 'Needed before this is active.')
    : detail.status === 'draft'
      ? 'Ready to set active.'
      : detail.status === 'inactive'
        ? 'Inactive. Set it active to put it back in the book.'
        : warnings.length > 0
          ? 'Active. A few details would still help the team.'
          : 'Ready for the team.';

  return (
    <div className={styles.editor}>
      <Link className={styles.back} to="/studio">← Back to studio</Link>
      <div className={styles.editorHead}>
        <div className={styles.identity}>
          <div className={styles.eyebrow}>{formatMapName(detail.map) || detail.map} · {detail.id}</div>
          <input
            className={styles.title}
            aria-label="Strategy name"
            value={detail.name}
            onChange={(event) => patch({ name: event.target.value })}
            onBlur={() => void saveFromState()}
          />
          <div className={styles.meta}>
            <span className={styles.badge}>{canonicalSide(detail.side)} side</span>
            <span className={styles.badge}>{detail.strategyType ? formatStrategyType(detail.strategyType) : 'Type not set'}</span>
            <span className={styles.badge}>{formatBuyType(detail.buyType)}</span>
            {detail.site?.trim() && <span className={styles.badge}>{detail.site} site</span>}
          </div>
          {detail.sourceName && <p className={styles.sourceLine}>Duplicated from {detail.sourceName}. Lineups stay shared.</p>}
        </div>
        <div className={styles.editorToolbar}>
          <div className={styles.statusRow} role="group" aria-label="Status">
            {(['draft', 'active', 'inactive'] as const).map((status) => (
              <button key={status} type="button" aria-pressed={detail.status === status} onClick={() => void saveFromState(status)}>
                {status === 'draft' ? 'Draft' : status === 'active' ? 'Active' : 'Inactive'}
              </button>
            ))}
          </div>
          <p className={styles.help}>{statusCopy}</p>
          <div className={styles.actions}>
            <Link to={`/strategies/${detail.id}`}>Player view</Link>
            <button type="button" onClick={() => void duplicate()}>Duplicate</button>
          </div>
        </div>
      </div>

      {error && <p className={styles.banner} role="alert">{error}</p>}

      <div className={styles.editorGrid}>
        <div className={styles.editorMain}>
          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <div>
                <h2>Strategy</h2>
                <p>The fields the team sees before they open a role.</p>
              </div>
              <a className={styles.panelLink} href="#section-roles">Roles</a>
            </div>
            <div className={styles.formPad}>
              <div className={styles.formGrid}>
                <label className={styles.field}><span>Map</span>
                  <select aria-label="Map" value={detail.map} onChange={(event) => {
                    const next = { ...detail, map: event.target.value };
                    setDetail(next);
                    void saveDetail(next);
                  }}>
                    {STRATOS_MAPS.map((map) => <option key={map.id} value={map.id}>{map.name}</option>)}
                    {!STRATOS_MAPS.some((map) => map.id === detail.map) && <option value={detail.map}>{detail.map}</option>}
                  </select>
                </label>
                <label className={styles.field}><span>Side</span>
                  <select aria-label="Side" value={canonicalSide(detail.side)} onChange={(event) => {
                    const next = { ...detail, side: event.target.value };
                    setDetail(next);
                    void saveDetail(next);
                  }}>
                    <option value="T">T</option>
                    <option value="CT">CT</option>
                  </select>
                </label>
                <label className={styles.field}><span>Type</span>
                  <select aria-label="Type" value={detail.strategyType ?? ''} onChange={(event) => {
                    const next = { ...detail, strategyType: event.target.value || null };
                    setDetail(next);
                    void saveDetail(next);
                  }}>
                    <option value="">Not set</option>
                    {STRATEGY_TYPES.map((type) => <option key={type} value={type}>{formatStrategyType(type)}</option>)}
                  </select>
                </label>
                <label className={styles.field}><span>Team buy</span>
                  <select aria-label="Buy" value={canonicalBuy(detail.buyType)} onChange={(event) => {
                    const next = { ...detail, buyType: event.target.value };
                    setDetail(next);
                    void saveDetail(next);
                  }}>
                    {BUY_TYPES.map((buy) => <option key={buy} value={buy}>{formatBuyType(buy)}</option>)}
                  </select>
                </label>
                <label className={styles.field}><span>Site</span>
                  <input aria-label="Site" value={detail.site ?? ''} placeholder="A, B or Mid" onChange={(event) => patch({ site: event.target.value })} onBlur={() => void saveFromState()} />
                </label>
                <label className={styles.field}><span>Route</span>
                  <input aria-label="Route" value={detail.route ?? ''} placeholder="Ramp" onChange={(event) => patch({ route: event.target.value })} onBlur={() => void saveFromState()} />
                </label>
                <label className={`${styles.field} ${styles.wide}`}><span>Description</span>
                  <textarea aria-label="Description" rows={3} value={detail.description ?? ''} onChange={(event) => patch({ description: event.target.value })} onBlur={() => void saveFromState()} />
                </label>
              </div>
              <p className={styles.help}>New roles start on the team buy. Change a role when its loadout differs. A draft can be incomplete.</p>
            </div>
          </section>

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <div>
                <h2>Round plan</h2>
                <p>The shared picture, before anyone looks at a role.</p>
              </div>
              <span className={styles.micro}>{steps.length} {steps.length === 1 ? 'step' : 'steps'}</span>
            </div>
            {steps.length === 0 ? (
              <div className={styles.formPad}>
                <div className={styles.emptyInline}>
                  <h3>No shared plan yet</h3>
                  <p>Three short steps are enough: how you set up, when you commit, and how you finish.</p>
                </div>
              </div>
            ) : (
              <div className={styles.stepList}>
                {steps.map((step, index) => (
                  <article key={step.id || index} className={styles.step}>
                    <div className={styles.stepIndex}>
                      <span className={styles.planNumber}>{String(step.sortOrder).padStart(2, '0')}</span>
                      <button type="button" onClick={() => void moveStep(index, -1)} disabled={index === 0}>Up</button>
                      <button type="button" onClick={() => void moveStep(index, 1)} disabled={index === steps.length - 1}>Down</button>
                    </div>
                    <div>
                      <label className={styles.field}><span>Title</span>
                        <input aria-label={`Step ${index + 1} title`} value={step.title} onChange={(event) => patchStep(step.id, { title: event.target.value })} onBlur={() => void saveStep(step.id)} />
                      </label>
                      <label className={styles.field}><span>Detail</span>
                        <textarea aria-label={`Step ${index + 1} body`} rows={3} value={step.body} onChange={(event) => patchStep(step.id, { body: event.target.value })} onBlur={() => void saveStep(step.id)} />
                      </label>
                      <button type="button" className={styles.textBtn} onClick={() => void removeStep(step.id)}>Remove step</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
            <div className={styles.panelFoot}>
              <button type="button" onClick={() => void addStep()}>Add step</button>
            </div>
          </section>

          <section className={styles.panel} id="section-roles">
            <div className={styles.panelHead}>
              <div>
                <h2>Roles and timeline</h2>
                <p>Each role is one player’s buy and their actions on the round clock.</p>
              </div>
              <span className={styles.micro}>{roles.length} {roles.length === 1 ? 'role' : 'roles'}</span>
            </div>
            <div className={styles.roleTabs}>
              <div role="tablist" aria-label="Roles">
                {roles.map((item) => (
                  <button key={item.id} type="button" role="tab" aria-selected={role?.id === item.id} onClick={() => setRoleId(item.id)}>
                    <span>{item.name || 'Untitled role'}</span>
                    <small>{formatBuyType(item.buyType)}</small>
                  </button>
                ))}
              </div>
              <button type="button" className={styles.addTab} onClick={() => void addRole()}>Add role</button>
            </div>
            {role ? (
              <div className={styles.roleBody}>
                <div className={styles.roleHead}>
                  <label className={styles.field}><span>Role name</span>
                    <input aria-label="Role name" value={role.name} onChange={(event) => patchRole(role.id, { name: event.target.value })} onBlur={() => void saveRole(role.id)} />
                  </label>
                  <div className={styles.roleTools}>
                    <label className={styles.field}><span>Role buy</span>
                      <select aria-label="Role buy" value={canonicalBuy(role.buyType)} onChange={(event) => void saveRole(role.id, event.target.value)}>
                        {BUY_TYPES.map((buy) => <option key={buy} value={buy}>{formatBuyType(buy)}</option>)}
                      </select>
                    </label>
                    {confirm === 'role' ? (
                      <>
                        <button type="button" className={styles.danger} onClick={() => void removeRole(role.id)}>Delete role</button>
                        <button type="button" onClick={() => setConfirm(null)}>Cancel</button>
                      </>
                    ) : (
                      <button type="button" className={styles.danger} onClick={() => setConfirm('role')}>Remove role</button>
                    )}
                  </div>
                </div>
                <p className={styles.blurb}>{roleBlurb(role.name)}</p>
                {confirm === 'role' && <p className={styles.fieldError}>Removing this role deletes the learning stored on it.</p>}
                <div className={styles.loadout}>
                  <span className={styles.micro}>Implied loadout</span>
                  {impliedChips(role).map((chip) => <span key={chip}>{chip}</span>)}
                </div>
                <p className={styles.help}>Taken from this role’s buy and its utility actions. Grenades are not stored as a separate list.</p>
                {role.actions.length === 0 ? (
                  <div className={styles.emptyInline}>
                    <h3>No actions on the clock</h3>
                    <p>Add what this role does, from 1:55 down to 0:00.</p>
                  </div>
                ) : (
                  <ol className={styles.actionList}>
                    {[...role.actions].sort((a, b) => b.roundTimeSecs - a.roundTimeSecs).map((action) => (
                      <ActionRow
                        key={action.id}
                        action={action}
                        lineups={mapLineups}
                        strategyMap={detail.map}
                        onPatch={(partial) => patchAction(role.id, action.id, partial)}
                        onSave={() => void saveAction(role.id, action.id)}
                        onDelete={() => void removeAction(role.id, action.id)}
                        onCreateLineup={() => setNewLineupFor(action.id)}
                      />
                    ))}
                  </ol>
                )}
                {role.actions.length > 0 && (
                  <p className={styles.help}>The round clock sets the order. Two actions on the same second stay in the order you added them.</p>
                )}
                {newLineupFor && role.actions.some((action) => action.id === newLineupFor) && (
                  <NewLineup
                    onCancel={() => setNewLineupFor(null)}
                    onCreate={(body) => void createLineup(role.id, newLineupFor, body)}
                  />
                )}
                <div className={styles.panelFoot}>
                  <button type="button" onClick={() => void addAction(role.id)}>Add action</button>
                </div>
              </div>
            ) : (
              <div className={styles.formPad}>
                <div className={styles.emptyInline}>
                  <h3>No roles yet</h3>
                  <p>Add a role for each player in the call. Five is common. The book does not require five.</p>
                </div>
              </div>
            )}
          </section>
        </div>

        <aside className={styles.sideStack}>
          <section className={styles.panel} id="readiness-panel">
            <div className={styles.panelHead}>
              <div>
                <h2>Readiness</h2>
                <p>{statusCopy}</p>
              </div>
            </div>
            <div className={styles.asideBody}>
              <p className={styles.summary}>{summary}</p>
              <ul className={styles.checkList} aria-label="Readiness">
                {problems.map((item) => <li key={item} className={styles.bad}>{item}</li>)}
                {warnings.map((item) => <li key={item} className={styles.warn}>{item}</li>)}
                {problems.length === 0 && warnings.length === 0 && <li className={styles.good}>Nothing else is blocking this call.</li>}
              </ul>
            </div>
          </section>
          <section className={styles.panel}>
            <div className={styles.panelHead}><h2>At a glance</h2></div>
            <dl className={styles.facts}>
              <div>
                <dt>Map</dt>
                <dd>{formatMapName(detail.map) || detail.map}<div className={styles.mapKey}>{detail.map}</div></dd>
              </div>
              <div><dt>Side</dt><dd>{sideName(detail.side) || '—'}</dd></div>
              <div><dt>Type</dt><dd>{detail.strategyType ? formatStrategyType(detail.strategyType) : 'Not set'}</dd></div>
              <div><dt>Team buy</dt><dd>{formatBuyType(detail.buyType) || '—'}</dd></div>
              <div><dt>Site</dt><dd>{detail.site?.trim() || '—'}</dd></div>
              <div><dt>Route</dt><dd>{detail.route?.trim() || '—'}</dd></div>
              <div><dt>Roles</dt><dd>{roles.length}</dd></div>
              <div><dt>Actions</dt><dd>{actionCount}</dd></div>
              <div><dt>Updated</dt><dd>{formatUpdated(detail.updatedAt) || '—'}</dd></div>
              <div className={styles.span}><dt>Id</dt><dd>{detail.id}</dd></div>
            </dl>
          </section>
          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <div>
                <h2>Lineups in this call</h2>
                <p>Edit a lineup in the library and every strategy that uses it updates.</p>
              </div>
            </div>
            <div className={styles.asideBody}>
              {callLineups.length === 0 ? (
                <p>No shared lineups yet. A utility action can point at one.</p>
              ) : (
                <ul className={styles.usedList}>
                  {callLineups.map((lineup) => (
                    <li key={lineup.id}>
                      <Link to="/lineups">{lineup.name}</Link>
                      <span className={utilityBadgeClass(lineup.utilityType)}>{utilityLabel(lineup.utilityType)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
          <div className={styles.note}>
            <div className={styles.eyebrow}>Remove from the book</div>
            <h3>Delete this strategy</h3>
            <p>Roles, the round plan and actions are deleted. Shared lineups stay in the library. Learning stored against these roles is deleted with them.</p>
            <button
              type="button"
              className={styles.danger}
              onClick={() => (confirm === 'strategy' ? void removeStrategy() : setConfirm('strategy'))}
            >
              {confirm === 'strategy' ? 'Confirm delete' : 'Delete strategy'}
            </button>
            {confirm === 'strategy' && <button type="button" onClick={() => setConfirm(null)}>Cancel</button>}
          </div>
        </aside>
      </div>
      <p className={styles.footerNote}>Times are seconds left on the round clock. 1:55 is 115.</p>
    </div>
  );

  async function saveFromState(status?: string) {
    const current = await readDetail();
    if (current) await saveDetail(current, status ?? current.status);
  }

  function patchStep(id: string, partial: Partial<StrategyDetail['planSteps'][number]>) {
    setDetail((current) => current && ({
      ...current,
      planSteps: current.planSteps.map((step) => step.id === id ? { ...step, ...partial } : step),
    }));
  }

  async function saveStep(id: string) {
    const current = await readDetail();
    const step = current?.planSteps.find((item) => item.id === id);
    if (!step) return;
    setError(null);
    try {
      await updatePlanStep(teamId, strategyId, id, { title: step.title, body: step.body, sortOrder: step.sortOrder });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the step');
    }
  }

  async function addStep() {
    setError(null);
    try {
      await createPlanStep(teamId, strategyId, { title: 'New step', body: '' });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add a step');
    }
  }

  async function removeStep(id: string) {
    setError(null);
    try {
      await deletePlanStep(teamId, strategyId, id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the step');
    }
  }

  async function moveStep(index: number, direction: -1 | 1) {
    if (!detail) return;
    const steps = [...detail.planSteps].sort((a, b) => a.sortOrder - b.sortOrder);
    const other = steps[index + direction];
    const current = steps[index];
    if (!other || !current) return;
    setError(null);
    try {
      await updatePlanStep(teamId, strategyId, current.id, { sortOrder: other.sortOrder, title: current.title, body: current.body });
      await updatePlanStep(teamId, strategyId, other.id, { sortOrder: current.sortOrder, title: other.title, body: other.body });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reorder the plan');
    }
  }

  function patchRole(id: string, partial: Partial<StrategyRoleDetail>) {
    setDetail((current) => current && ({
      ...current,
      roles: current.roles.map((item) => item.id === id ? { ...item, ...partial } : item),
    }));
  }

  async function saveRole(id: string, buyType?: string) {
    const current = await readDetail();
    const item = current?.roles.find((roleItem) => roleItem.id === id);
    if (!item) return;
    const buy = buyType ?? item.buyType;
    if (buyType) patchRole(id, { buyType });
    setError(null);
    try {
      await updateRole(teamId, strategyId, id, { name: item.name, sortOrder: item.sortOrder, buyType: buy });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the role');
    }
  }

  async function addRole() {
    setError(null);
    try {
      const created = await createRole(teamId, strategyId, { name: 'New role' });
      await reload();
      setRoleId(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add a role');
    }
  }

  async function removeRole(id: string) {
    setError(null);
    try {
      await deleteRole(teamId, strategyId, id);
      setConfirm(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the role');
    }
  }

  function patchAction(ownerId: string, actionId: string, partial: Partial<StrategyActionDetail>) {
    setDetail((current) => current && ({
      ...current,
      roles: current.roles.map((item) => item.id === ownerId ? {
        ...item,
        actions: item.actions.map((action) => action.id === actionId ? { ...action, ...partial } : action),
      } : item),
    }));
  }

  async function saveAction(ownerId: string, actionId: string) {
    const current = await readDetail();
    const action = current?.roles.find((item) => item.id === ownerId)?.actions.find((item) => item.id === actionId);
    if (!action) return;
    setError(null);
    try {
      const saved = await updateAction(teamId, strategyId, ownerId, actionId, {
        roundTimeSecs: action.roundTimeSecs,
        durationSecs: action.durationSecs,
        actionType: action.actionType,
        description: action.description ?? '',
        utilityUseId: action.utilityUse?.id ?? '',
      });
      patchAction(ownerId, actionId, saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the action');
    }
  }

  async function addAction(ownerId: string) {
    setError(null);
    try {
      await createAction(teamId, strategyId, ownerId, { actionType: 'move', roundTimeSecs: 115, description: '' });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add an action');
    }
  }

  async function removeAction(ownerId: string, actionId: string) {
    setError(null);
    try {
      await deleteAction(teamId, strategyId, ownerId, actionId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the action');
    }
  }

  async function createLineup(ownerId: string, actionId: string, body: { name: string; utilityType: string; description: string; url: string }) {
    if (!detail) return;
    setError(null);
    try {
      const created = await createUtilityUse(teamId, { ...body, map: detail.map });
      await updateAction(teamId, strategyId, ownerId, actionId, { utilityUseId: created.id, actionType: 'utility' });
      setNewLineupFor(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the lineup');
    }
  }

  async function duplicate() {
    setError(null);
    try {
      const copy = await duplicateStrategy(teamId, strategyId);
      navigate(`/studio/${copy.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not duplicate the strategy');
    }
  }

  async function removeStrategy() {
    setError(null);
    try {
      await deleteStrategy(teamId, strategyId);
      navigate('/studio');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the strategy');
    }
  }

  function readDetail() {
    return new Promise<StrategyDetail | null>((resolve) => {
      setDetail((current) => {
        resolve(current);
        return current;
      });
    });
  }
}

function utilityLabel(type: string) {
  if (type === 'he') return 'HE';
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : type;
}

function utilityBadgeClass(type: string) {
  const tone = type === 'smoke' ? styles.smoke
    : type === 'flash' ? styles.flash
      : type === 'molotov' ? styles.molotov
        : type === 'he' ? styles.he
          : type === 'decoy' ? styles.decoy
            : undefined;
  return tone ? `${styles.badge} ${tone}` : styles.badge;
}

function usedLineups(detail: StrategyDetail): UtilityUseDetail[] {
  const seen = new Set<string>();
  const items: UtilityUseDetail[] = [];
  for (const role of detail.roles) {
    for (const action of role.actions) {
      const lineup = action.utilityUse;
      if (!lineup || seen.has(lineup.id)) continue;
      seen.add(lineup.id);
      items.push(lineup);
    }
  }
  return items;
}

function impliedChips(role: StrategyRoleDetail): string[] {
  const chips = [formatBuyType(role.buyType) || 'Buy not set'];
  const seen = new Set<string>();
  for (const action of role.actions) {
    const type = action.utilityUse?.utilityType;
    if (action.actionType !== 'utility' || !type || seen.has(type)) continue;
    seen.add(type);
    chips.push(utilityLabel(type));
  }
  return chips;
}

function ActionRow({
  action,
  lineups,
  strategyMap,
  onPatch,
  onSave,
  onDelete,
  onCreateLineup,
}: {
  action: StrategyActionDetail;
  lineups: UtilityUseRecord[];
  strategyMap: string;
  onPatch: (partial: Partial<StrategyActionDetail>) => void;
  onSave: () => void;
  onDelete: () => void;
  onCreateLineup: () => void;
}) {
  const lineup = action.utilityUse;
  const mismatched = Boolean(lineup?.map && mapKey(lineup.map) !== mapKey(strategyMap));
  return (
    <li>
      <label className={`${styles.field} ${styles.clock}`}><span>Clock</span>
        <input
          aria-label="Clock"
          defaultValue={formatClock(action.roundTimeSecs)}
          key={action.roundTimeSecs}
          onBlur={(event) => {
            const parsed = parseClock(event.target.value);
            if (parsed == null) return;
            onPatch({ roundTimeSecs: parsed });
            onSave();
          }}
        />
      </label>
      <div className={styles.actionCard}>
        <div className={styles.actionGrid}>
          <label className={styles.field}><span>Action</span>
            <select aria-label="Action type" value={action.actionType} onChange={(event) => { onPatch({ actionType: event.target.value }); onSave(); }}>
              {ACTION_TYPES.map((type) => <option key={type} value={type}>{formatActionType(type)}</option>)}
            </select>
          </label>
          <label className={styles.field}><span>Duration</span>
            <input
              aria-label="Duration"
              placeholder="Secs"
              defaultValue={action.durationSecs ?? ''}
              key={`${action.id}-duration-${action.durationSecs ?? 'none'}`}
              onBlur={(event) => {
                const parsed = parseDuration(event.target.value);
                if (parsed === undefined) return;
                onPatch({ durationSecs: parsed });
                onSave();
              }}
            />
          </label>
          <button type="button" className={styles.textBtn} onClick={onDelete}>Remove</button>
        </div>
        <label className={styles.field}><span>What they do</span>
          <textarea aria-label="Action detail" rows={2} value={action.description ?? ''} onChange={(event) => onPatch({ description: event.target.value })} onBlur={onSave} />
        </label>
        {action.actionType === 'hold' && action.durationSecs == null && <p className={styles.help}>A hold should say how long it lasts.</p>}
        {action.actionType === 'utility' && (
          <div className={lineup ? `${styles.lineupChip} ${mismatched ? styles.mismatch : ''}` : styles.lineupEmpty}>
            {lineup ? (
              <div>
                <span className={utilityBadgeClass(lineup.utilityType)}>{utilityLabel(lineup.utilityType)}</span>
                <strong>{lineup.name}</strong>
                <p>{lineup.description || 'No throw notes yet.'}</p>
                {mismatched && lineup.map && <p className={styles.fieldError}>This lineup is on {formatMapName(lineup.map) || lineup.map}.</p>}
              </div>
            ) : (
              <p>Pick a saved lineup for this map. The same throw can be used by other strategies.</p>
            )}
            <label className={styles.field}><span>{lineup ? 'Change' : 'Lineup'}</span>
              <select
                aria-label="Lineup"
                value={lineup?.id ?? ''}
                onChange={(event) => {
                  if (event.target.value === 'new') {
                    onCreateLineup();
                    return;
                  }
                  const next = lineups.find((item) => item.id === event.target.value);
                  onPatch({
                    utilityUse: next ? {
                      id: next.id,
                      map: next.map,
                      name: next.name,
                      description: next.description,
                      url: next.url,
                      utilityType: next.utilityType,
                    } : null,
                  });
                  onSave();
                }}
              >
                <option value="">Choose a lineup</option>
                {lineups.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                {lineup && !lineups.some((item) => item.id === lineup.id) && (
                  <option value={lineup.id}>{lineup.name}</option>
                )}
                <option value="new">Create lineup</option>
              </select>
            </label>
          </div>
        )}
      </div>
    </li>
  );
}

function NewLineup({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (body: { name: string; utilityType: string; description: string; url: string }) => void;
}) {
  const [name, setName] = useState('');
  const [utilityType, setUtilityType] = useState('smoke');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  return (
    <form className={styles.lineupForm} onSubmit={(event) => { event.preventDefault(); onCreate({ name, utilityType, description, url }); }}>
      <h3>New lineup</h3>
      <label>Name<input aria-label="New lineup name" value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label>Grenade
        <select aria-label="New lineup grenade" value={utilityType} onChange={(event) => setUtilityType(event.target.value)}>
          {UTILITY_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
      </label>
      <label>Notes<textarea aria-label="New lineup notes" value={description} onChange={(event) => setDescription(event.target.value)} /></label>
      <label>Learning link<input aria-label="New lineup link" value={url} onChange={(event) => setUrl(event.target.value)} /></label>
      <button type="submit">Save lineup</button>
      <button type="button" onClick={onCancel}>Cancel</button>
    </form>
  );
}
